import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, BookOpen, Download, FileText, Loader2, Plus, Trash2, Save,
  X, Sparkles, Lock, Archive,
} from "lucide-react";
import { toast } from "sonner";
import {
  getCompany,
  listRegisters,
  getRegister,
  addRegisterEntry,
  updateRegisterEntry,
  deleteRegisterEntry,
  downloadRegisterFile,
  downloadCompliancePack,
} from "@/lib/api";
import { EntryForm, formatCell } from "@/components/registers/EntryForm";
import { FolioRegister } from "@/components/registers/FolioRegister";
import { Sh1Modal, Sh4Modal } from "@/components/registers/ShareFormModals";

export default function Registers() {
  const { id: companyId } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [registers, setRegisters] = useState([]); // list summary
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // active register key
  const [data, setData] = useState(null); // full payload for active register
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [showSh1, setShowSh1] = useState(false);
  const [showSh4, setShowSh4] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    setLoading(true);
    Promise.all([getCompany(companyId), listRegisters(companyId)])
      .then(([cRes, lRes]) => {
        setCompany(cRes.data);
        setRegisters(lRes.data.registers || []);
        if (lRes.data.registers?.length && !active) {
          setActive(lRes.data.registers[0].key);
        }
      })
      .catch(() => toast.error("Could not load registers"))
      .finally(() => setLoading(false));
  }, [companyId, active]);

  useEffect(() => {
    if (!companyId || !active) return;
    setBusy(true);
    getRegister(companyId, active)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load register entries"))
      .finally(() => setBusy(false));
  }, [companyId, active]);

  const activeMeta = useMemo(
    () => registers.find((r) => r.key === active),
    [registers, active],
  );

  const reloadCounts = async () => {
    try {
      const r = await listRegisters(companyId);
      setRegisters(r.data.registers || []);
    } catch (err) {
      // Counts refresh is non-fatal — the active register and its data
      // panel keep working; the sidebar chip just stays stale until the
      // next reload. Log for diagnostics.
      console.warn("Failed to refresh register counts:", err);
    }
  };

  const reloadEntries = async () => {
    setBusy(true);
    try {
      const r = await getRegister(companyId, active);
      setData(r.data);
    } finally {
      setBusy(false);
    }
  };

  const handleAdd = async () => {
    try {
      await addRegisterEntry(companyId, active, draft);
      toast.success("Entry added");
      setDraft({});
      setAdding(false);
      await Promise.all([reloadEntries(), reloadCounts()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add entry");
    }
  };

  const handleUpdate = async (entryId) => {
    try {
      await updateRegisterEntry(companyId, active, entryId, editDraft);
      toast.success("Entry updated");
      setEditingId(null);
      setEditDraft({});
      await reloadEntries();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update entry");
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm("Delete this entry? This cannot be undone.")) return;
    try {
      await deleteRegisterEntry(companyId, active, entryId);
      toast.success("Entry deleted");
      await Promise.all([reloadEntries(), reloadCounts()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const handleDownload = async (fmt) => {
    try {
      await downloadRegisterFile(companyId, active, fmt);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch {
      toast.error("Download failed");
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-10 flex items-center gap-3 text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading registers…
      </div>
    );
  }

  if (!company) return null;

  return (
    <div className="max-w-7xl mx-auto px-6 py-8" data-testid="registers-page">
      <button
        onClick={() => navigate(`/companies/${companyId}`)}
        className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-6"
      >
        <ArrowLeft className="w-4 h-4" /> Back to {company.name}
      </button>

      <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-blue-700 mb-1 flex items-center gap-1.5">
            <BookOpen className="w-3.5 h-3.5" /> Statutory Registers
          </div>
          <h1 className="font-heading text-3xl font-light tracking-tight">{company.name}</h1>
          <p className="text-sm text-slate-600 mt-1">
            All registers under Companies Act 2013 · auto-populated from members, directors and resolutions where applicable.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={async () => {
              try {
                await downloadCompliancePack(companyId);
                toast.success("Compliance pack downloaded");
              } catch {
                toast.error("Could not generate compliance pack");
              }
            }}
            className="px-3 py-2 bg-blue-700 text-white text-sm hover:bg-blue-800 flex items-center gap-1.5"
            data-testid="compliance-pack-btn"
            title="One-click ZIP of every statutory register PDF — ideal for pre-AGM handover."
          >
            <Archive className="w-3.5 h-3.5" /> Pre-AGM Pack (ZIP)
          </button>
          <button
            onClick={() => setShowSh1(true)}
            className="px-3 py-2 border border-slate-300 text-sm hover:border-blue-700 hover:text-blue-700"
            data-testid="form-sh1-open"
          >
            <FileText className="w-3.5 h-3.5 inline mr-1.5" /> Issue SH-1 Share Certificate
          </button>
          <button
            onClick={() => setShowSh4(true)}
            className="px-3 py-2 border border-slate-300 text-sm hover:border-blue-700 hover:text-blue-700"
            data-testid="form-sh4-open"
          >
            <FileText className="w-3.5 h-3.5 inline mr-1.5" /> Generate SH-4 Transfer Deed
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        {/* Sidebar */}
        <aside className="border border-slate-200 bg-white" data-testid="registers-sidebar">
          <ul>
            {registers.map((r) => {
              const isActive = r.key === active;
              return (
                <li key={r.key}>
                  <button
                    onClick={() => setActive(r.key)}
                    className={`w-full px-4 py-3 text-left border-b border-slate-100 transition ${
                      isActive ? "bg-blue-50 border-l-2 border-l-blue-700" : "hover:bg-slate-50"
                    }`}
                    data-testid={`register-tab-${r.key}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">
                        {r.form_number}
                      </span>
                      <span className="text-[10px] text-slate-400">{r.total_count}</span>
                    </div>
                    <div className="text-sm font-medium text-slate-900 mt-0.5 leading-tight">
                      {r.label}
                    </div>
                    {r.has_auto_source && (
                      <div className="text-[10px] text-blue-700 mt-1 flex items-center gap-1">
                        <Sparkles className="w-2.5 h-2.5" /> Auto-linked
                      </div>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        {/* Main panel — `min-w-0` is critical: without it, a wide table
            inside an `overflow-x-auto` child pushes the grid column wider
            than 1fr and the page itself gains horizontal scroll instead of
            the table. */}
        <main className="space-y-4 min-w-0">
          {!data ? (
            <div className="text-slate-500 text-sm flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading register…
            </div>
          ) : data.register.mode === "folio" ? (
            <FolioRegister
              companyId={companyId}
              registerKey={active}
              data={data}
              activeMeta={activeMeta}
              members={company?.members || []}
              onReload={async () => { await Promise.all([reloadEntries(), reloadCounts()]); }}
              onDownload={handleDownload}
            />
          ) : (
            <>
              {/* Header — buttons moved to the LEFT (next to title) so they
                  are always visible without horizontal scroll. */}
              <div className="border border-slate-200 bg-white p-5">
                <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid={`register-${active}-actions`}>
                  <button
                    onClick={() => handleDownload("pdf")}
                    className="px-3 py-1.5 border border-slate-300 text-xs hover:border-blue-700 hover:text-blue-700 flex items-center gap-1.5 bg-white"
                    data-testid={`register-${active}-download-pdf`}
                  >
                    <Download className="w-3 h-3" /> PDF
                  </button>
                  <button
                    onClick={() => handleDownload("docx")}
                    className="px-3 py-1.5 border border-slate-300 text-xs hover:border-blue-700 hover:text-blue-700 flex items-center gap-1.5 bg-white"
                    data-testid={`register-${active}-download-docx`}
                  >
                    <Download className="w-3 h-3" /> WORD
                  </button>
                  <button
                    onClick={() => {
                      setAdding(true);
                      setDraft({});
                    }}
                    className="px-3 py-1.5 bg-blue-700 text-white text-xs flex items-center gap-1.5 hover:bg-blue-800"
                    data-testid={`register-${active}-add`}
                  >
                    <Plus className="w-3 h-3" /> Add Entry
                  </button>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">
                    {data.register.form_number}
                  </div>
                  <h2 className="text-xl font-semibold text-slate-900 mt-0.5">
                    {data.register.label}
                  </h2>
                  <p className="text-xs text-slate-500 italic mt-1">{data.register.section}</p>
                </div>
                {activeMeta && (
                  <div className="mt-3 flex items-center gap-4 text-[11px] text-slate-500">
                    <span>Total: <strong className="text-slate-800">{activeMeta.total_count}</strong></span>
                    {activeMeta.auto_count > 0 && (
                      <span>Auto-derived: <strong className="text-blue-700">{activeMeta.auto_count}</strong></span>
                    )}
                    <span>Manual: <strong className="text-slate-800">{activeMeta.manual_count}</strong></span>
                  </div>
                )}
              </div>

              {/* Add-row form */}
              {adding && (
                <div
                  className="border border-blue-200 bg-blue-50/50 p-5"
                  data-testid={`register-${active}-add-form`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-sm">New Entry</h3>
                    <button onClick={() => setAdding(false)} className="text-slate-400 hover:text-slate-900">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <EntryForm
                    columns={data.register.columns}
                    value={draft}
                    onChange={setDraft}
                    members={company?.members || []}
                  />
                  <div className="mt-3 flex items-center gap-2">
                    <button
                      onClick={handleAdd}
                      className="px-3 py-1.5 bg-blue-700 text-white text-xs flex items-center gap-1.5 hover:bg-blue-800"
                      data-testid="entry-save"
                    >
                      <Save className="w-3 h-3" /> Save
                    </button>
                    <button
                      onClick={() => setAdding(false)}
                      className="px-3 py-1.5 border border-slate-300 text-xs hover:bg-slate-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Entries table */}
              <div className="border border-slate-200 bg-white overflow-x-auto">
                {busy ? (
                  <div className="p-8 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                  </div>
                ) : data.entries.length === 0 ? (
                  <div className="p-8 text-center text-sm text-slate-500" data-testid="register-empty">
                    No entries yet. Click <strong>Add Entry</strong> above or, where applicable, entries will appear automatically from members / directors / resolutions.
                  </div>
                ) : (
                  <table className="w-full text-sm" data-testid="register-table">
                    <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-600">
                      <tr>
                        <th className="px-3 py-2 text-left w-12">#</th>
                        {data.register.columns.map((c) => (
                          <th key={c.key} className="px-3 py-2 text-left whitespace-nowrap">
                            {c.label}
                          </th>
                        ))}
                        <th className="px-3 py-2 w-24">Source</th>
                        <th className="px-3 py-2 w-20"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.entries.map((e, idx) => {
                        const isAuto = e._auto;
                        const isEditing = editingId === e.id;
                        return (
                          <tr
                            key={e.id || e._id || idx}
                            className={`border-t border-slate-100 ${isAuto ? "bg-blue-50/30" : ""}`}
                            data-testid={`register-row-${idx}`}
                          >
                            <td className="px-3 py-2 text-slate-500">{idx + 1}</td>
                            {isEditing ? (
                              <td colSpan={data.register.columns.length}>
                                <div className="p-3">
                                  <EntryForm
                                    columns={data.register.columns}
                                    value={editDraft}
                                    onChange={setEditDraft}
                                    members={company?.members || []}
                                  />
                                  <div className="mt-2 flex gap-2">
                                    <button
                                      onClick={() => handleUpdate(e.id)}
                                      className="px-3 py-1 bg-blue-700 text-white text-xs"
                                    >
                                      Save
                                    </button>
                                    <button
                                      onClick={() => {
                                        setEditingId(null);
                                        setEditDraft({});
                                      }}
                                      className="px-3 py-1 border text-xs"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              </td>
                            ) : (
                              data.register.columns.map((c) => (
                                <td key={c.key} className="px-3 py-2 align-top max-w-xs">
                                  <div className="truncate" title={String(e[c.key] ?? "")}>
                                    {formatCell(c.type, e[c.key])}
                                  </div>
                                </td>
                              ))
                            )}
                            {!isEditing && (
                              <>
                                <td className="px-3 py-2 align-top">
                                  {isAuto ? (
                                    <span
                                      className="inline-flex items-center gap-1 text-[10px] text-blue-700 bg-blue-50 px-1.5 py-0.5"
                                      title={e._source || "Auto"}
                                    >
                                      <Lock className="w-2.5 h-2.5" /> Auto
                                    </span>
                                  ) : (
                                    <span className="text-[10px] text-slate-400">Manual</span>
                                  )}
                                </td>
                                <td className="px-3 py-2 align-top text-right">
                                  {!isAuto && (
                                    <div className="flex gap-1 justify-end">
                                      <button
                                        onClick={() => {
                                          setEditingId(e.id);
                                          setEditDraft(e);
                                        }}
                                        className="text-[10px] text-slate-500 hover:text-blue-700 px-1.5 py-0.5"
                                        data-testid={`entry-edit-${idx}`}
                                      >
                                        Edit
                                      </button>
                                      <button
                                        onClick={() => handleDelete(e.id)}
                                        className="text-slate-400 hover:text-red-600 p-1"
                                        data-testid={`entry-delete-${idx}`}
                                        title="Delete"
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </button>
                                    </div>
                                  )}
                                </td>
                              </>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              <p className="text-[11px] text-slate-500 italic">
                Auto-derived rows (blue background) come from your saved Members / Directors / Resolutions. Edit those on the company page or via the resolution detail. Add manual entries to capture data the system doesn't yet know about (folio numbers, distinctive numbers, certificate numbers, etc.).
              </p>
            </>
          )}
        </main>
      </div>

      {showSh1 && (
        <Sh1Modal company={company} onClose={() => setShowSh1(false)} />
      )}
      {showSh4 && (
        <Sh4Modal company={company} onClose={() => setShowSh4(false)} />
      )}
    </div>
  );
}
